/**
 * Deterministic avatar kinematics for the walkable port (Onda 6) — the on-foot twin of `stepShipBody`.
 *
 * `stepWalkerBody` is pure given (body, input, dt) and is called with the SAME code on the server
 * (truth) and the client (prediction of the LOCAL avatar). No wall-clock, no Math.random, in-place
 * mutation for zero GC churn. Movement is omnidirectional (a joystick vector), not steered like a hull:
 * the avatar accelerates toward a target velocity and its heading eases toward the direction it moves.
 */
import { WALK } from '@piratas/protocol';
import { clamp, normAngle } from './math.js';

export interface WalkerBody {
  /** Port-local plane position (meters, within [0, PORT.SIZE]). */
  x: number;
  y: number;
  /** Plane velocity (m/s). */
  vx: number;
  vy: number;
  /** Radians facing; 0 = +x (east). Eases toward the move direction. */
  heading: number;
}

export interface WalkerInput {
  /** Desired move along +x (east), -1..1. */
  moveX: number;
  /** Desired move along +y (north), -1..1. */
  moveY: number;
}

export function makeWalkerBody(x: number, y: number, heading = Math.PI / 2): WalkerBody {
  return { x, y, vx: 0, vy: 0, heading };
}

export const NEUTRAL_WALK: WalkerInput = Object.freeze({ moveX: 0, moveY: 0 });

/** A solid circular prop on the plaza plane (stall / tavern / well / crate). */
export interface WalkerObstacle { x: number; y: number; r: number }

/** A solid axis-aligned RECTANGLE on the room plane (Onda 8: the captain's-house mezzanine deck + its stair
 *  buttress — a raised platform the walker can never enter). Unlike a circle obstacle, a rect models a flush
 *  architectural block with clean edges. Shared shell DATA (HOUSE_SHELL.solidRects) so server truth + client
 *  prediction push out of it identically. */
export interface WalkerRect { minX: number; minY: number; maxX: number; maxY: number }

/**
 * Advance a walker body by one fixed timestep. Mutates `b` in place. Pure + deterministic — the server
 * and the client's local prediction pass the same input, so prediction stays byte-for-byte with authority
 * (any FP wobble is corrected by the reconcile snap each snapshot).
 */
export function stepWalkerBody(b: WalkerBody, input: WalkerInput, dt: number): void {
  // clamp the move vector into the unit disk (diagonal isn't faster than cardinal)
  let mx = input.moveX, my = input.moveY;
  const m2 = mx * mx + my * my;
  if (m2 > 1) { const inv = 1 / Math.sqrt(m2); mx *= inv; my *= inv; }
  const moving = m2 > 1e-4;

  // target velocity, approached at ACCEL (moving) or DRAG (stopping)
  const tx = mx * WALK.SPEED, ty = my * WALK.SPEED;
  const rate = clamp((moving ? WALK.ACCEL : WALK.DRAG) * dt, 0, 1);
  b.vx += (tx - b.vx) * rate;
  b.vy += (ty - b.vy) * rate;

  // integrate position
  b.x += b.vx * dt;
  b.y += b.vy * dt;

  // heading eases toward the move direction while moving (never snaps → smooth turn)
  if (moving) {
    const target = Math.atan2(my, mx);
    let d = normAngle(target - b.heading);
    const max = WALK.TURN_RATE * dt;
    if (d > max) d = max; else if (d < -max) d = -max;
    b.heading = normAngle(b.heading + d);
  }
}

/**
 * Push the walker out of any solid circle it overlaps, then clamp it inside the plaza walls. Runs AFTER
 * `stepWalkerBody` on BOTH server and client prediction with the SAME obstacle list + plaza size, so the
 * local avatar never tunnels a stall and prediction stays == authority. Pure + deterministic, alloc-free.
 * The avatar is approximated by a circle of `r`; it slides tangentially along a prop / wall.
 */
export function collidePort(b: WalkerBody, obstacles: readonly WalkerObstacle[], r: number, size: number): void {
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i]!;
    const min = o.r + r;
    if (min <= 0) continue;
    const dx = b.x - o.x, dy = b.y - o.y;
    const d2 = dx * dx + dy * dy;
    if (d2 >= min * min) continue;                 // not overlapping
    const d = Math.sqrt(d2);
    const nx = d > 1e-4 ? dx / d : 1;              // outward normal (degenerate centre -> +x)
    const ny = d > 1e-4 ? dy / d : 0;
    b.x = o.x + nx * min;                          // project to the prop boundary
    b.y = o.y + ny * min;
    const vn = b.vx * nx + b.vy * ny;             // kill the inward velocity → slide, don't stick/tunnel
    if (vn < 0) { b.vx -= vn * nx; b.vy -= vn * ny; }
  }
  // invisible plaza walls (a square [r, size-r])
  const lo = r, hi = size - r;
  if (b.x < lo) { b.x = lo; if (b.vx < 0) b.vx = 0; }
  else if (b.x > hi) { b.x = hi; if (b.vx > 0) b.vx = 0; }
  if (b.y < lo) { b.y = lo; if (b.vy < 0) b.vy = 0; }
  else if (b.y > hi) { b.y = hi; if (b.vy > 0) b.vy = 0; }
}

/**
 * Onda 8: the RECTANGULAR twin of `collidePort` for the captain's house interior (`HOUSE.WIDTH×HOUSE.DEPTH`).
 * Identical closed-form circle-push + slide, but clamps the avatar into a `w`×`d` rectangle (the four cabin
 * walls) instead of a square. Runs on BOTH server and client prediction with the SAME obstacle list + shell
 * dims, so the local avatar stays == authority. Pure + deterministic + alloc-free.
 */
export function collideRoom(b: WalkerBody, obstacles: readonly WalkerObstacle[], r: number, w: number, d: number, rects: readonly WalkerRect[] = []): void {
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i]!;
    const min = o.r + r;
    if (min <= 0) continue;
    const dx = b.x - o.x, dy = b.y - o.y;
    const d2 = dx * dx + dy * dy;
    if (d2 >= min * min) continue;
    const dist = Math.sqrt(d2);
    const nx = dist > 1e-4 ? dx / dist : 1;
    const ny = dist > 1e-4 ? dy / dist : 0;
    b.x = o.x + nx * min;
    b.y = o.y + ny * min;
    const vn = b.vx * nx + b.vy * ny;
    if (vn < 0) { b.vx -= vn * nx; b.vy -= vn * ny; }
  }
  // Solid rectangles (Onda 8 mezzanine deck + stair buttress): keep the avatar CIRCLE out of each AABB, which
  // is inflated by r. If the centre lands inside (a live avatar caught atop a newly-solid rect), push it out
  // along the least-penetration face — a graceful clamp toward the open floor, never through a wall.
  for (let i = 0; i < rects.length; i++) {
    const q = rects[i]!;
    const loRX = q.minX - r, hiRX = q.maxX + r, loRY = q.minY - r, hiRY = q.maxY + r;
    if (b.x <= loRX || b.x >= hiRX || b.y <= loRY || b.y >= hiRY) continue; // outside the inflated rect
    const pL = b.x - loRX, pR = hiRX - b.x, pB = b.y - loRY, pT = hiRY - b.y; // penetration toward each face
    const m = Math.min(pL, pR, pB, pT);
    if (m === pL) { b.x = loRX; if (b.vx > 0) b.vx = 0; }
    else if (m === pR) { b.x = hiRX; if (b.vx < 0) b.vx = 0; }
    else if (m === pB) { b.y = loRY; if (b.vy > 0) b.vy = 0; }
    else { b.y = hiRY; if (b.vy < 0) b.vy = 0; }
  }
  const loX = r, hiX = w - r, loY = r, hiY = d - r;
  if (b.x < loX) { b.x = loX; if (b.vx < 0) b.vx = 0; }
  else if (b.x > hiX) { b.x = hiX; if (b.vx > 0) b.vx = 0; }
  if (b.y < loY) { b.y = loY; if (b.vy < 0) b.vy = 0; }
  else if (b.y > hiY) { b.y = hiY; if (b.vy > 0) b.vy = 0; }
}
